import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SuppliersPage from "@/pages/SuppliersPage";
import CustomersPage from "@/pages/CustomersPage";
import InventoryPage from "@/pages/InventoryPage";
import UsersPage from "@/pages/UsersPage";
import QuotesPage from "@/pages/QuotesPage";
import InvoicesPage from "@/pages/InvoicesPage";
import SettingsPage from "@/pages/SettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import ApprovalQueuePage from "@/pages/ApprovalQueuePage";
import PurchaseOrdersPage from "@/pages/PurchaseOrdersPage";
import ExpensesPage from "@/pages/ExpensesPage";
import QuoteRequestsPage from "@/pages/QuoteRequestsPage";
import EmailImportsPage from "@/pages/EmailImportsPage";
import CreditNotesPage from "@/pages/CreditNotesPage";
import CustomerPaymentsPage from "@/pages/CustomerPaymentsPage";
import SupplierPaymentsPage from "@/pages/SupplierPaymentsPage";
import StatementsPage from "@/pages/StatementsPage";
import AgingReportsPage from "@/pages/AgingReportsPage";
import PaymentBatchesPage from "@/pages/PaymentBatchesPage";
import DeliveryNotesPage from "@/pages/DeliveryNotesPage";
import PackingSlipsPage from "@/pages/PackingSlipsPage";
import PriceListsPage from "@/pages/PriceListsPage";
import PaymentCapturePage from "@/pages/PaymentCapturePage";
import ExpenseCategoriesPage from "@/pages/ExpenseCategoriesPage";
import ReceiptScanningPage from "@/pages/ReceiptScanningPage";
import StockAdjustmentsPage from "@/pages/StockAdjustmentsPage";
import LowStockAlertsPage from "@/pages/LowStockAlertsPage";
import ReportsHubPage from "@/pages/ReportsHubPage";
import SalesReportsPage from "@/pages/SalesReportsPage";
import PurchaseReportsPage from "@/pages/PurchaseReportsPage";
import InventoryReportsPage from "@/pages/InventoryReportsPage";
import FinancialReportsPage from "@/pages/FinancialReportsPage";
import ProfitAnalysisPage from "@/pages/ProfitAnalysisPage";
import ExportDataPage from "@/pages/ExportDataPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import InventoryCategoriesPage from "@/pages/InventoryCategoriesPage";
import CustomerPurchaseOrdersPage from "@/pages/CustomerPurchaseOrdersPage";
import DashboardConfigPage from "@/pages/DashboardConfigPage";
import VATReturnsPage from "@/pages/VATReturnsPage";
import VATAuditTrailPage from "@/pages/VATAuditTrailPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-[#E63946] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionId = hash.split("session_id=")[1];

    if (sessionId) {
      api.post("/auth/google-callback", { session_id: sessionId })
        .then((res) => {
          setUser(res.data);
          navigate("/dashboard", { replace: true, state: { user: res.data } });
        })
        .catch(() => {
          navigate("/login", { replace: true });
        });
    } else {
      navigate("/login", { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-[#E63946] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Authenticating...</p>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();

  // Check URL fragment for session_id (Google Auth callback)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="suppliers/:id" element={<SuppliersPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomersPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/:id" element={<InventoryPage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="quotes/:id" element={<QuotesPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/:id" element={<InvoicesPage />} />
        <Route path="reports-legacy" element={<ReportsPage />} />
        <Route path="approvals" element={<ApprovalQueuePage />} />
        <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
        <Route path="purchase-orders/:id" element={<PurchaseOrdersPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="quote-requests" element={<QuoteRequestsPage />} />
        <Route path="quote-requests/:id" element={<QuoteRequestsPage />} />
        <Route path="email-imports" element={<EmailImportsPage />} />
        <Route path="email-imports/:id" element={<EmailImportsPage />} />
        <Route path="credit-notes" element={<CreditNotesPage />} />
        <Route path="credit-notes/:id" element={<CreditNotesPage />} />
        <Route path="customer-payments" element={<CustomerPaymentsPage />} />
        <Route path="customer-payments/:id" element={<CustomerPaymentsPage />} />
        <Route path="supplier-payments" element={<SupplierPaymentsPage />} />
        <Route path="supplier-payments/:id" element={<SupplierPaymentsPage />} />
        <Route path="statements" element={<StatementsPage />} />
        <Route path="aging-reports" element={<AgingReportsPage />} />
        <Route path="payment-batches" element={<PaymentBatchesPage />} />
        <Route path="payment-batches/:id" element={<PaymentBatchesPage />} />
        <Route path="delivery-notes" element={<DeliveryNotesPage />} />
        <Route path="delivery-notes/:id" element={<DeliveryNotesPage />} />
        <Route path="packing-slips" element={<PackingSlipsPage />} />
        <Route path="packing-slips/:id" element={<PackingSlipsPage />} />
        <Route path="price-lists" element={<PriceListsPage />} />
        <Route path="price-lists/:id" element={<PriceListsPage />} />
        <Route path="payment-capture" element={<PaymentCapturePage />} />
        <Route path="expense-categories" element={<ExpenseCategoriesPage />} />
        <Route path="receipt-scanning" element={<ReceiptScanningPage />} />
        <Route path="stock-adjustments" element={<StockAdjustmentsPage />} />
        <Route path="low-stock" element={<LowStockAlertsPage />} />
        <Route path="reports" element={<ReportsHubPage />} />
        <Route path="reports/sales" element={<SalesReportsPage />} />
        <Route path="reports/purchases" element={<PurchaseReportsPage />} />
        <Route path="reports/inventory" element={<InventoryReportsPage />} />
        <Route path="reports/financial" element={<FinancialReportsPage />} />
        <Route path="reports/profit" element={<ProfitAnalysisPage />} />
        <Route path="reports/export" element={<ExportDataPage />} />
        <Route path="inventory-categories" element={<InventoryCategoriesPage />} />
        <Route path="customer-purchase-orders" element={<CustomerPurchaseOrdersPage />} />
        <Route path="customer-purchase-orders/:id" element={<CustomerPurchaseOrdersPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="settings/dashboard" element={<DashboardConfigPage />} />
        <Route path="vat-returns" element={<VATReturnsPage />} />
        <Route path="vat-returns/:returnId" element={<VATReturnsPage />} />
        <Route path="vat-audit-trail" element={<VATAuditTrailPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton duration={4000} />
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
